/**
 * React Query hooks for notification preferences API.
 *
 * Exports:
 * - useNotificationPreferences: fetch preferences
 * - useUpdateNotificationPreferences: update preferences
 * - useIncrementPromptCount: increment onboarding prompt count
 * - useGenerateTelegramCode: generate a Telegram link code
 * - useDisconnectTelegram: disconnect Telegram
 * - useTelegramStatus: poll for Telegram connection status
 * - useReEnableChannel: re-enable a disabled notification channel
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { CACHE_TIMES } from '@/config/cacheTimes';
import type {
  NotificationPreferences,
  NotificationPreferencesUpdate,
  NotificationChannel,
  TelegramLinkResponse,
  TelegramStatusResponse,
} from '@/types/notifications';

async function fetchPreferences(): Promise<NotificationPreferences> {
  return api.get('/api/notifications/preferences');
}

async function updatePreferences(
  data: NotificationPreferencesUpdate
): Promise<NotificationPreferences> {
  return api.put('/api/notifications/preferences', data);
}

async function incrementPromptCount(): Promise<NotificationPreferences> {
  return api.post('/api/notifications/preferences/increment-prompt');
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notification-preferences'],
    queryFn: fetchPreferences,
    staleTime: CACHE_TIMES.DATA,
    // Note: the payload includes the raw telegram_chat_id. Persisting it to
    // localStorage is an accepted risk for now: it's own-device storage, the
    // persisted cache has a 24h maxAge, and clearPersistedCache wipes it on
    // logout. Stricter alternative if this ever changes: strip the field at
    // dehydrate time, or have the API return only the masked variant.
    meta: { persist: true },
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePreferences,
    onSuccess: (data) => {
      queryClient.setQueryData(['notification-preferences'], data);
    },
  });
}

export function useIncrementPromptCount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: incrementPromptCount,
    onSuccess: (data) => {
      queryClient.setQueryData(['notification-preferences'], data);
    },
  });
}


// =============================================================================
// Channel Re-enable
// =============================================================================

async function reEnableChannel(channel: NotificationChannel): Promise<NotificationPreferences> {
  return api.post(`/api/notifications/preferences/${channel}/re-enable`);
}

export function useReEnableChannel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reEnableChannel,
    onSuccess: (data) => {
      queryClient.setQueryData(['notification-preferences'], data);
    },
  });
}


// =============================================================================
// Telegram Linking
// =============================================================================

async function generateTelegramLinkCode(): Promise<TelegramLinkResponse> {
  return api.post('/api/notifications/preferences/telegram/generate-code');
}

async function disconnectTelegram(): Promise<void> {
  await api.delete('/api/notifications/preferences/telegram');
}

async function fetchTelegramStatus(): Promise<TelegramStatusResponse> {
  return api.get('/api/notifications/preferences/telegram/status');
}

export function useGenerateTelegramCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: generateTelegramLinkCode,
    onSuccess: () => {
      // Invalidate preferences to refresh telegram status
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });
}

export function useDisconnectTelegram() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: disconnectTelegram,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      queryClient.invalidateQueries({ queryKey: ['telegram-status'] });
    },
  });
}

/**
 * Poll for Telegram connection status.
 *
 * @param enabled - Whether polling is active (set to true when link modal is open)
 * @param intervalMs - Polling interval in milliseconds (default: 3000)
 */
export function useTelegramStatus(enabled = false, intervalMs = 3000) {
  return useQuery({
    queryKey: ['telegram-status'],
    queryFn: fetchTelegramStatus,
    enabled,
    refetchInterval: enabled ? intervalMs : false,
  });
}
